import React, { useEffect, useMemo, useRef, useState } from 'react';
import List from '@mui/material/List';
import {
  CircularProgress,
  Box,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  ListItemButton,
  ListItemText,
  Typography,
  Snackbar,
} from '@mui/material'; // 로딩/에러 UI 추가
import SearchIcon from '@mui/icons-material/Search';

import MenuItem from './MenuItem';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as domainApi from '../../../lib/api/domain'; // API 함수 추가
import { fetchMenuFavs, saveMenuFavs } from '../../../lib/api/menuFav';
import { matchMenuText } from '../../../lib/chosung';

// 아코디언에서 "마지막으로 연 그룹"과 즐겨찾기 펼침 상태는 사용자별 서버 저장까지는 필요 없는
// 가벼운 UI 상태라 localStorage 에만 둔다(기기별로 달라도 무방). 실패해도 메뉴 자체는 동작해야
// 하므로 항상 try/catch 로 감싼다.
const LAST_GROUP_KEY = 'ksm.menu.lastOpenGroup';
const FAV_OPEN_KEY = 'ksm.menu.favOpen';

const readLocalStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // 프라이빗 모드/용량 초과 등은 무시 - 이 값은 "기억하면 편한" 정도라 치명적이지 않다.
  }
};

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');

const normalizeLink = (link) => {
  if (!link) return '';
  return link.startsWith('/') ? link : `/${link}`;
};

/** 매치된 구간을 <mark> 로 감싸 하이라이트한다. match 가 없으면 원문 그대로. */
const renderHighlighted = (text, match) => {
  if (!match || !text) return text;
  const { index, length } = match;
  const before = text.slice(0, index);
  const mid = text.slice(index, index + length);
  const after = text.slice(index + length);
  return (
    <>
      {before}
      <Box
        component="mark"
        sx={{ bgcolor: '#fff59d', color: 'inherit', borderRadius: '2px' }}
      >
        {mid}
      </Box>
      {after}
    </>
  );
};

const MenuList = ({ onMenuSelect, focusSearchToken }) => {
  const { user } = useSelector(({ auth }) => ({
    user: auth.user,
  }));
  const location = useLocation();
  const navigate = useNavigate();

  // 상태 관리
  const [filterMenus, setFilterMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // B) 메뉴 검색
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);

  // A) 아코디언 - 한 번에 하나의 그룹만 펼침(즐겨찾기는 별도)
  const [openGroupId, setOpenGroupId] = useState(() => readLocalStorage(LAST_GROUP_KEY));
  const [favOpen, setFavOpen] = useState(() => {
    const saved = readLocalStorage(FAV_OPEN_KEY);
    return saved === null ? true : saved === '1';
  });

  // C) 즐겨찾기 - 서버 저장(menuUuid 순서 목록)
  const [favorites, setFavorites] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // 사용자 권한 기반 메뉴 로드
  useEffect(() => {
    const loadUserMenus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // 서버에서 사용자 권한 기반 메뉴 조회
        const response = await domainApi.checkAuth(); // 또는 fetch('/api/check/auth')

        // 응답 구조에 맞게 데이터 추출
        if (response && response.data.list) {
          setFilterMenus(response.data.list);
        } else {
          setFilterMenus([]);
        }

      } catch (error) {
        console.error('메뉴 로드 실패:', error);
        setError('메뉴를 불러오는데 실패했습니다.');
        setFilterMenus([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserMenus();
  }, [user]); // user가 변경될 때마다 다시 로드

  // 즐겨찾기 목록 로드(사용자별 서버 저장). 실패해도 메뉴 자체는 떠야 하므로 콘솔 로그만 남긴다.
  useEffect(() => {
    if (!user) {
      setFavorites([]);
      return;
    }
    fetchMenuFavs()
      .then((res) => setFavorites(res?.data?.list || []))
      .catch((err) => console.error('즐겨찾기 메뉴를 불러오지 못했습니다:', err));
  }, [user]);

  /**
   * 지금 화면에 해당하는 메뉴 하나만 고른다.
   *
   * NavLink 는 주소가 앞부분만 같아도 켜진다.
   * 그래서 /targetmng/kpi-setting 에 있으면 "KPI 관리" 뿐 아니라 상위 경로인
   * "목표관리(/targetmng)" 까지 같이 칠해져 메뉴가 중복 선택된 것처럼 보였다.
   *
   * 가장 길게 일치하는 것 하나만 남긴다. 상세 화면(/chancemng/{id}/...)에서도
   * 목록 메뉴가 계속 켜져 있어야 하므로 완전 일치로 바꾸지는 않는다.
   */
  const activeLink = useMemo(() => {
    const path = location.pathname;
    let best = '';
    // 하위 메뉴는 items 에 담겨 온다(MenuItem 도 items 를 그린다).
    // children 만 훑으면 하위 메뉴가 통째로 빠져 아무것도 안 칠해진다.
    const walk = (list) => {
      (list || []).forEach((item) => {
        const link = item?.link;
        if (link) {
          const to = link.startsWith('/') ? link : `/${link}`;
          if ((path === to || path.startsWith(`${to}/`)) && to.length > best.length) {
            best = to;
          }
        }
        walk(item?.items);
      });
    };
    walk(filterMenus);
    return best;
  }, [filterMenus, location.pathname]);

  // 지금 activeLink 를 품고 있는 그룹(대메뉴)의 id. 상세 화면 등 어느 메뉴에도 안 걸리면 null —
  // 이때는 "마지막으로 연 그룹"을 그대로 유지한다(아래 effect).
  const activeGroupId = useMemo(() => {
    if (!activeLink) return null;
    for (const group of filterMenus) {
      if (normalizeLink(group.link) === activeLink) return group.id;
      const hit = (group.items || []).some((item) => normalizeLink(item.link) === activeLink);
      if (hit) return group.id;
    }
    return null;
  }, [filterMenus, activeLink]);

  // 로드/라우트 변경 때 activeGroupId 가 있으면 그 그룹을 연다(이전 그룹은 자동으로 닫힘 - 단일 상태라서).
  useEffect(() => {
    if (activeGroupId) {
      setOpenGroupId(activeGroupId);
      writeLocalStorage(LAST_GROUP_KEY, activeGroupId);
    }
    // activeGroupId 가 없으면(목록에 없는 상세 화면 등) 마지막 상태를 그대로 둔다 - 의도적으로 아무것도 안 함.
  }, [activeGroupId]);

  const handleToggleGroup = (groupId) => {
    setOpenGroupId((prev) => {
      const next = prev === groupId ? null : groupId;
      writeLocalStorage(LAST_GROUP_KEY, next || '');
      return next;
    });
  };

  const handleToggleFavOpen = () => {
    setFavOpen((prev) => {
      const next = !prev;
      writeLocalStorage(FAV_OPEN_KEY, next ? '1' : '0');
      return next;
    });
  };

  // 검색/즐겨찾기가 공통으로 쓰는 평탄화된 서브메뉴 목록(그룹 정보 포함).
  // checkAuth 응답의 items[].uuid 는 MENU_UUID(UserMenuService 참고) - 즐겨찾기 조인 키.
  const flatItems = useMemo(() => {
    const list = [];
    filterMenus.forEach((group) => {
      (group.items || []).forEach((item) => {
        list.push({ ...item, groupId: group.id, groupTitle: group.title });
      });
    });
    return list;
  }, [filterMenus]);

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);
  const isFavorite = (item) => !!item?.uuid && favoriteSet.has(item.uuid);

  // 저장된 순서대로 실제 메뉴 항목을 매칭한다. 권한이 없어진(=flatItems 에 없는) 즐겨찾기는
  // 조용히 빠진다 - 서버의 즐겨찾기 자체는 지우지 않는다(요구사항: "조용히 숨김").
  const favoriteItems = useMemo(() => {
    const byUuid = new Map(flatItems.map((item) => [item.uuid, item]));
    return favorites.map((uuid) => byUuid.get(uuid)).filter(Boolean);
  }, [favorites, flatItems]);

  const handleToggleFavorite = (item) => {
    if (!item?.uuid) return;
    const already = favorites.includes(item.uuid);
    const next = already ? favorites.filter((uuid) => uuid !== item.uuid) : [...favorites, item.uuid];
    const prevSnapshot = favorites;

    setFavorites(next); // 낙관적 업데이트 - 서버 응답을 기다리지 않고 바로 반영
    saveMenuFavs(next).catch((err) => {
      console.error('즐겨찾기 저장 실패:', err);
      setFavorites(prevSnapshot); // 롤백
      setSnackbar({ open: true, message: '즐겨찾기 저장에 실패했습니다. 다시 시도해 주세요.' });
    });
  };

  // B) 검색 결과: 메뉴 제목 또는 그룹(대메뉴) 제목에 매치되면 포함한다(초성 매칭 포함, chosung.js).
  const searchResults = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    const results = [];
    flatItems.forEach((item) => {
      const titleMatch = matchMenuText(item.title, q);
      if (titleMatch) {
        results.push({ item, match: titleMatch, matchOn: 'title' });
        return;
      }
      const groupMatch = matchMenuText(item.groupTitle, q);
      if (groupMatch) {
        results.push({ item, match: groupMatch, matchOn: 'group' });
      }
    });
    return results;
  }, [flatItems, query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Ctrl+K / ⌘K 는 DrawerTemplate(항상 떠 있음)이 받아서 드로어를 먼저 열고, 여기엔
  // "포커스 해라" 신호(토큰 증가)만 내려온다. 모바일이면 그 사이 MenuList 가 새로 마운트되므로
  // 이 effect 는 마운트 시점과 무관하게 토큰이 바뀔 때마다 포커스를 시도한다.
  const isFirstFocusToken = useRef(true);
  useEffect(() => {
    if (isFirstFocusToken.current) {
      isFirstFocusToken.current = false;
      return;
    }
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [focusSearchToken]);

  const handleSearchNavigate = (item) => {
    if (!item?.link) return;
    setQuery('');
    navigate(normalizeLink(item.link));
    if (onMenuSelect) window.setTimeout(onMenuSelect, 0);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, Math.max(searchResults.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const sel = searchResults[selectedIndex];
      if (sel) handleSearchNavigate(sel.item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      searchInputRef.current?.blur();
    }
  };

  const searchBox = (
    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
      <TextField
        inputRef={searchInputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleSearchKeyDown}
        placeholder="메뉴 검색"
        size="small"
        fullWidth
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Chip
                label={isMac ? '⌘K' : 'Ctrl K'}
                size="small"
                variant="outlined"
                sx={{ color: '#9e9e9e', borderColor: '#e0e0e0', height: 22, fontSize: '0.7rem' }}
              />
            </InputAdornment>
          ),
        }}
        sx={{ bgcolor: '#fff', borderRadius: 1 }}
      />
    </Box>
  );

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // 메뉴가 없는 경우
  if (!filterMenus || filterMenus.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">사용 가능한 메뉴가 없습니다.</Alert>
      </Box>
    );
  }

  const isSearching = query.trim().length > 0;

  return (
    <>
      {searchBox}

      {isSearching ? (
        <List sx={{ width: '100%', bgcolor: '#fafafa', color: '#424242' }} component="nav">
          <Typography variant="caption" sx={{ display: 'block', color: '#757575', px: 2, pb: 0.5 }}>
            검색 결과 {searchResults.length}개 · ↑↓ 이동, Enter 열기
          </Typography>
          {searchResults.length === 0 ? (
            <Typography variant="body2" sx={{ px: 2, py: 2, color: '#9e9e9e' }}>
              일치하는 메뉴가 없습니다
            </Typography>
          ) : (
            searchResults.map((result, index) => (
              <ListItemButton
                key={result.item.uuid || index}
                selected={index === selectedIndex}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => handleSearchNavigate(result.item)}
                sx={{ mx: 1, my: 0.25, borderRadius: '4px' }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" component="div">
                      {result.matchOn === 'title'
                        ? renderHighlighted(result.item.title, result.match)
                        : result.item.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" component="div" sx={{ color: '#9e9e9e' }}>
                      {result.matchOn === 'group'
                        ? renderHighlighted(result.item.groupTitle, result.match)
                        : result.item.groupTitle}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))
          )}
        </List>
      ) : (
        <List sx={{ width: '100%', bgcolor: '#fafafa', color: '#424242' }} component="nav">
          {/* C) 즐겨찾기 - 아코디언과 독립적으로 펼침/접힘 상태를 가진다 */}
          <MenuItem
            menu={{
              id: '__favorites__',
              title:
                favoriteItems.length > 0 && !favOpen
                  ? `즐겨찾기 (${favoriteItems.length})`
                  : '즐겨찾기',
              items: favoriteItems,
            }}
            variant="favorites"
            open={favOpen}
            onToggleOpen={handleToggleFavOpen}
            activeLink={activeLink}
            onMenuSelect={onMenuSelect}
            isFavorite={isFavorite}
            onToggleFavorite={handleToggleFavorite}
          />

          {filterMenus.map((menu, index) => (
            <MenuItem
              menu={menu}
              key={menu.id || index}
              activeLink={activeLink}
              onMenuSelect={onMenuSelect}
              open={menu.id === openGroupId}
              onToggleOpen={() => handleToggleGroup(menu.id)}
              isFavorite={isFavorite}
              onToggleFavorite={handleToggleFavorite}
            />
          ))}
        </List>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSnackbar({ open: false, message: '' })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MenuList;
